import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Alert, Typography,
} from '@mui/material';
import userService from '../../services/userService';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  members?: { first_name: string; last_name: string; id?: string }[];
}

const CATEGORIES = [
  { value: 'sexual_harassment', label: 'Sexual Harassment' },
  { value: 'assault', label: 'Physical Assault or Threat' },
  { value: 'stalking', label: 'Stalking / Unwanted Contact' },
  { value: 'intoxication', label: 'Intoxication (Drunk/High)' },
  { value: 'disruptive', label: 'Disruptive Behavior' },
  { value: 'other', label: 'Other' },
];

export default function ReportAbuseModal({ open, onClose, eventId, members = [] }: Props) {
  const [category, setCategory] = useState('');
  const [reportedUserId, setReportedUserId] = useState('');
  const [description, setDescription] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!category || !description || !reportedUserId) {
      setError('Please fill all required fields'); return;
    }
    setLoading(true); setError('');
    try {
      await userService.reportAbuse({ reported_user_id: reportedUserId, event_id: eventId, category, description, anonymous });
      setSuccess(true);
    } catch (e: any) { console.warn('[ReportAbuseModal] submit report failed', e); setError(e.message || 'Failed to submit report'); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Report Submitted</DialogTitle>
        <DialogContent>
          <Typography>Thank you. We take safety seriously and will review within 24 hours.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Report Abuse</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Who are you reporting? *</InputLabel>
          <Select value={reportedUserId} onChange={e => setReportedUserId(e.target.value)} label="Who are you reporting? *">
            {members.map((m, i) => (
              <MenuItem key={m.id || i} value={m.id || `unknown-${i}`}>{m.first_name} {m.last_name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Category *</InputLabel>
          <Select value={category} onChange={e => setCategory(e.target.value)} label="Category *">
            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField fullWidth multiline rows={4} label="Description *" value={description}
          onChange={e => setDescription(e.target.value)} placeholder="Please describe what happened..."
          inputProps={{ maxLength: 2000 }} sx={{ mb: 2 }} />

        <FormControlLabel
          control={<Switch checked={anonymous} onChange={e => setAnonymous(e.target.checked)} />}
          label="Submit anonymously (your identity won't be shared with the reported person)"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
